import { Plug } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

const PROVIDER_FIELDS = {
  meta_whatsapp: ['accessToken', 'phoneNumberId', 'wabaId', 'appSecret'],
  meta_instagram: ['accessToken', 'pageId', 'appSecret'],
  meta_messenger: ['accessToken', 'pageId', 'appSecret'],
  exotel: ['sid', 'apiKey', 'apiToken', 'subdomain', 'callerId'],
  stripe: ['apiKey', 'webhookSecret'],
  razorpay: ['keyId', 'keySecret', 'webhookSecret'],
  cashfree: ['clientId', 'clientSecret', 'webhookSecret'],
  payu: ['merchantKey', 'merchantSalt'],
  ccavenue: ['merchantId', 'accessCode', 'workingKey'],
  clearbit: ['apiKey'],
  hunter: ['apiKey'],
  apollo: ['apiKey'],
  google_places: ['apiKey'],
  slack: ['teamId', 'botToken', 'signingSecret', 'approvalChannel'],
  teams: ['appId', 'appPassword'],
  gst_einvoice: ['gspProvider', 'apiBaseUrl', 'cancelApiUrl', 'gstin', 'clientId', 'clientSecret', 'username', 'password'],
};

// Shown above the credential form for providers that need the company to paste
// a webhook URL into a third-party app config before the connection works.
const PROVIDER_WEBHOOK_PATHS = {
  slack: '/api/slack/events',
  teams: '/api/teams/messages',
  razorpay: '/api/crm/payment-webhooks/razorpay',
  stripe: '/api/crm/payment-webhooks/stripe',
  cashfree: '/api/crm/payment-webhooks/cashfree',
  payu: '/api/crm/payment-webhooks/payu',
  ccavenue: '/api/crm/payment-webhooks/ccavenue',
};

const PROVIDER_ICONS = {
  meta_whatsapp: '💬', meta_instagram: '📷', meta_messenger: '🟦',
  exotel: '📱', stripe: '💳', razorpay: '🔵', cashfree: '🟢', payu: '🟠', ccavenue: '🟣',
  clearbit: '🔍', hunter: '🎯', apollo: '🚀', google_places: '📍',
  slack: '🗨️', teams: '👥',
  gst_einvoice: '🧾',
};

const FIELD_LABELS = {
  baseURL: 'Base URL (optional — for Groq, Azure OpenAI, or other OpenAI-compatible providers)',
  workspaceId: 'Workspace ID (optional — required only if your API key is not scoped to a workspace)',
  approvalChannel: 'Approval channel ID (optional, e.g. C0123ABCD: AI approval requests are posted there with Approve / Reject buttons)',
  sid: 'Account SID',
  apiToken: 'API Token',
  subdomain: 'API Subdomain (e.g. "api", or your account\'s cluster)',
  callerId: 'Caller ID (Exotel virtual number)',
  gspProvider: 'GSP Name (e.g. ClearTax, Cygnet, NIC)',
  apiBaseUrl: 'IRN Generation API URL (from your GSP)',
  cancelApiUrl: 'IRN Cancellation API URL (optional — defaults to Base URL + /cancel)',
  gstin: 'Your Company GSTIN',
  clientId: 'Client ID',
  username: 'Username',
  keyId: 'Key ID',
  keySecret: 'Key Secret',
  webhookSecret: 'Webhook Secret (from your gateway dashboard\'s webhook config)',
  merchantKey: 'Merchant Key',
  merchantSalt: 'Merchant Salt',
  merchantId: 'Merchant ID',
  accessCode: 'Access Code',
  workingKey: 'Working Key',
};

const PASSWORD_FIELDS = ['apiKey', 'apiToken', 'accessToken', 'secret', 'authToken', 'clientSecret', 'botToken', 'signingSecret', 'appPassword', 'password', 'webhookSecret', 'keySecret', 'merchantSalt', 'workingKey'];

const STATUS_COLORS = {
  ok: 'text-[#2E844A] bg-[#EBF7E6]',
  error: 'text-[#BA0517] bg-[#FEF1EE]',
  untested: 'text-[#706E6B] bg-[#F7F9FB]',
};

export default function IntegrationsPage() {
  const [providers, setProviders] = useState([]);
  const [connected, setConnected] = useState({});
  const [editProvider, setEditProvider] = useState(null);
  const [formData, setFormData] = useState({});
  const [testing, setTesting] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    api.get('/integrations/providers').then(r => setProviders(r.data.data || []));
    api.get('/integrations').then(r => {
      const map = {};
      for (const c of r.data.data || []) map[c.provider] = c;
      setConnected(map);
    });
  }, []);

  const openEdit = (provider) => {
    setEditProvider(provider);
    setFormData({});
  };

  const save = async () => {
    if (!editProvider) return;
    setSaving(editProvider);
    try {
      await api.put(`/integrations/${editProvider}`, { credentials: formData });
      const { data } = await api.get('/integrations');
      const map = {};
      for (const c of data.data || []) map[c.provider] = c;
      setConnected(map);
      setEditProvider(null);
    } finally { setSaving(''); }
  };

  const disconnect = async (provider) => {
    await api.delete(`/integrations/${provider}`);
    setConnected(c => { const n = { ...c }; delete n[provider]; return n; });
  };

  const testConnection = async (provider) => {
    setTesting(provider);
    try {
      const { data } = await api.post(`/integrations/${provider}/test`);
      const { data: fresh } = await api.get('/integrations');
      const map = {};
      for (const c of fresh.data || []) map[c.provider] = c;
      setConnected(map);
      if (!data.data.ok) alertService.error('Error', `Test failed: ${data.data.error}`);
    } finally { setTesting(''); }
  };

  const fields = editProvider ? (PROVIDER_FIELDS[editProvider] || ['apiKey']) : [];

  return (
    <div className={PAGE}>
      <PageHeader title="Integrations" icon={Plug} subtitle="Connect your external services. Credentials are encrypted at rest." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map(({ id, label }) => {
          const rec = connected[id];
          const status = rec?.lastTestStatus || 'untested';
          return (
            <div key={id} className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-col gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{PROVIDER_ICONS[id] || '🔌'}</span>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-[#2E2E2E]">{label}</h3>
                  {rec ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[status]}`}>
                      {status === 'ok' ? '✓ Connected' : status === 'error' ? '✗ Error' : '○ Untested'}
                    </span>
                  ) : (
                    <span className="text-xs text-[#706E6B]">Not connected</span>
                  )}
                </div>
              </div>
              {rec?.lastTestError && (
                <p className="text-xs text-[#BA0517] truncate">{rec.lastTestError}</p>
              )}
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => openEdit(id)}
                  className="flex-1 text-xs bg-[#0176D3] hover:bg-[#014486] text-white py-1.5 rounded font-medium">
                  {rec ? 'Update' : 'Connect'}
                </button>
                {rec && (
                  <>
                    <button onClick={() => testConnection(id)} disabled={testing === id}
                      className="text-[13px] border border-[#E5E5E5] text-[#5C5C5C] hover:bg-[#F7F9FB] px-3 py-1.5 rounded disabled:opacity-50">
                      {testing === id ? '...' : 'Test'}
                    </button>
                    <button onClick={() => disconnect(id)}
                      className="text-[13px] border border-[#FDDDE3] text-[#BA0517] hover:bg-[#FEF1EE] px-3 py-1.5 rounded">
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editProvider && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
          <div className="bg-white shadow-sm w-full max-w-md p-4 sm:p-6 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-[16px] font-bold text-[#2E2E2E] mb-1">
              {PROVIDER_ICONS[editProvider]} {editProvider.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </h2>
            <p className="text-xs text-[#706E6B] mb-5">Credentials are encrypted with AES-256 before storage</p>
            {PROVIDER_WEBHOOK_PATHS[editProvider] && (
              <div className="mb-4 p-3 bg-[#EEF4FF] border border-[#D8E6FE] rounded-lg">
                <p className="text-xs text-[#706E6B] mb-1">Paste this URL into your {editProvider === 'slack' ? 'Slack app\'s Event Subscriptions' : 'Azure Bot\'s messaging endpoint'} config:</p>
                <code className="block text-xs text-[#0176D3] break-all">{window.location.origin}{PROVIDER_WEBHOOK_PATHS[editProvider]}</code>
              </div>
            )}
            <div className="space-y-3">
              {fields.map(field => (
                <div key={field}>
                  <label className="text-[12px] text-[#5C5C5C] mb-1 block capitalize">{FIELD_LABELS[field] || field.replace(/([A-Z])/g, ' $1')}</label>
                  <input
                    type={PASSWORD_FIELDS.includes(field) ? 'password' : 'text'}
                    value={formData[field] || ''}
                    onChange={e => setFormData(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={`Enter ${field}`}
                    className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[#E5E5E5]">
              <button onClick={() => setEditProvider(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">
                Cancel
              </button>
              <button onClick={save} disabled={saving === editProvider}
                className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50">
                {saving === editProvider ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
